import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router';
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  Clock,
  Home as HomeIcon,
  Map as MapIcon,
  MapPin,
  Search,
  SearchX,
  Store,
  X,
} from 'lucide-react';
import { useListingsParams } from '../../hooks/useListingParams';
import { useDebounce } from '../../hooks/useDebounce';
import { useTranslation } from 'react-i18next';
import { getProperties } from '../../api/listing/listingApi';
import { highlightMatch } from '../../helpers/highlightMatch';
import { listingPath } from '../../helpers/listingPath';
import { thumbUrl } from '../../helpers/imageUrl';
import { useMediaQuery } from '../../chat/hooks/useMediaQuery';
import { useBackHandler } from '../../chat/native/backButton';
import { SEO_CATEGORIES, SEO_LANGS, hubPath, type LangCode } from '../data/regions';

interface ListingsSearchProps {
  className?: string;
  placeholder?: string;
  ariaHidden?: boolean;
  ariaLabel?: string;
  /** Renders its own search icon + clear button + focus ring — a
   *  self-contained "search bar" look. Off by default so callers that
   *  already draw their own chrome keep a bare input to style themselves. */
  withChrome?: boolean;
  /** Live results while typing: real matching listings (title, price,
   *  location, photo) from the same endpoint the page queries. Phones get a
   *  full-screen search with recent queries and categories; wider screens a
   *  dropdown under the field. */
  withSuggestions?: boolean;
  /** «Show all» / Enter. Default: write `q` into the current page's params. */
  onSubmitQuery?: (q: string) => void;
}

const REGION_KEY: Record<string, string> = {
  BISHKEK: 'regions.bishkek',
  CHUI: 'regions.chuy',
  YSSYK_KOL: 'regions.yssyk_kol',
  NARYN: 'regions.naryn',
  JALAL_ABAD: 'regions.jalal-abad',
  OSH: 'regions.osh',
  BATKEN: 'regions.batken',
};

const CATEGORY_ICONS: Record<string, typeof Building2> = {
  APARTMENT: Building2,
  HOUSE: HomeIcon,
  PLOT: MapIcon,
  COMMERCIAL: Store,
};

interface SuggestionItem {
  id: number;
  title: string;
  price: string;
  currency?: string;
  image?: string;
  images?: string[];
  region?: string;
  district?: string;
  type?: string;
}

const MIN_CHARS = 2;
const SUGGEST_LIMIT = 8;

// ─── recent queries (this device) ────────────────────────────────────────────

const RECENT_KEY = 'turaq-search-recent';
const RECENT_MAX = 8;

function readRecent(): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string').slice(0, RECENT_MAX) : [];
  } catch {
    return [];
  }
}
function writeRecent(list: string[]) {
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
  } catch {
    /* private mode — history is a nicety */
  }
}
function pushRecent(q: string) {
  const v = q.trim();
  if (v.length < MIN_CHARS) return;
  writeRecent([v, ...readRecent().filter((x) => x.toLowerCase() !== v.toLowerCase())]);
}

// ─── live results ────────────────────────────────────────────────────────────

function useSuggestions(query: string, enabled: boolean) {
  const [items, setItems] = useState<SuggestionItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [settledFor, setSettledFor] = useState('');
  const requestId = useRef(0);

  useEffect(() => {
    const q = query.trim();
    if (!enabled || q.length < MIN_CHARS) {
      requestId.current++;
      setItems([]);
      setTotal(0);
      setLoading(false);
      setSettledFor('');
      return;
    }
    const id = ++requestId.current;
    setLoading(true);
    getProperties({ search: q, offset: SUGGEST_LIMIT, page: 1 } as any)
      .then((res) => {
        if (id !== requestId.current) return;
        const list = Array.isArray(res?.list) ? (res.list as SuggestionItem[]) : [];
        setItems(list);
        setTotal(Math.max(Number(res?.total) || 0, list.length));
      })
      .catch(() => {
        if (id !== requestId.current) return;
        setItems([]);
        setTotal(0);
      })
      .finally(() => {
        if (id !== requestId.current) return;
        setLoading(false);
        setSettledFor(q);
      });
  }, [query, enabled]);

  return { items, total, loading, settled: settledFor === query.trim() && !loading };
}

function usePriceLabel() {
  const { t } = useTranslation();
  return (item: SuggestionItem) => {
    if (item.price === '0' || !item.price) return t('createListing.negotiable.label', 'Договорная');
    const kgs = item.currency === 'KGS';
    const som = t('createListing.currency.somLabel', { defaultValue: 'сом' });
    return `${Math.round(Number(item.price)).toLocaleString('ru-RU')} ${kgs ? som : '$'}`;
  };
}

function useLocationLabel() {
  const { t } = useTranslation();
  return (item: SuggestionItem) =>
    item.district
      ? t(`districts.${item.district}`, item.district)
      : item.region
        ? t(REGION_KEY[item.region] ?? '', item.region)
        : '';
}

function Thumb({ item, size }: { item: SuggestionItem; size: number }) {
  const src = item.image || item.images?.[0];
  const Icon = CATEGORY_ICONS[String(item.type || '').toUpperCase()] ?? Building2;
  return (
    <span
      className="flex shrink-0 items-center justify-center overflow-hidden rounded-xl bg-slate-100 text-slate-300"
      style={{ width: size, height: size }}
    >
      {src ? (
        <img src={thumbUrl(src, size)} alt="" loading="lazy" decoding="async" className="h-full w-full object-cover" />
      ) : (
        <Icon size={size * 0.4} />
      )}
    </span>
  );
}

function ResultSkeleton({ size }: { size: number }) {
  return (
    <div className="flex items-center gap-3 px-4 py-2.5" aria-hidden="true">
      <span className="shrink-0 animate-pulse rounded-xl bg-slate-100" style={{ width: size, height: size }} />
      <span className="flex-1 space-y-2">
        <span className="block h-3.5 w-4/5 animate-pulse rounded-full bg-slate-100" />
        <span className="block h-3 w-2/5 animate-pulse rounded-full bg-slate-100" />
      </span>
    </div>
  );
}

function NoResults() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center px-6 py-10 text-center">
      <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
        <SearchX size={26} />
      </span>
      <p className="mt-3 text-[15px] font-bold text-slate-900">{t('search.noResults')}</p>
      <p className="mt-1 max-w-[280px] text-[13px] leading-snug text-slate-500">{t('search.noResultsHint')}</p>
    </div>
  );
}

// ─── component ───────────────────────────────────────────────────────────────

export function ListingsSearch({
  className,
  placeholder,
  ariaHidden = false,
  ariaLabel,
  withChrome = false,
  withSuggestions = false,
  onSubmitQuery,
}: ListingsSearchProps) {
  const { t } = useTranslation();
  const [params, updateParams] = useListingsParams();
  const phone = useMediaQuery('(max-width: 767px)');

  const [value, setValue] = useState(params.q || '');
  const debounced = useDebounce(value, 350);
  const [overlay, setOverlay] = useState(false);

  // An outside change of ?q (back/forward, «Сбросить фильтры») shows up in the field.
  useEffect(() => {
    setValue((v) => (v.trim() === (params.q || '').trim() ? v : params.q || ''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.q]);

  useEffect(() => {
    if ((params.q || '') !== debounced.trim()) updateParams({ q: debounced.trim() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);

  const submit = useCallback(
    (q: string) => {
      const v = q.trim();
      pushRecent(v);
      setValue(v);
      if (onSubmitQuery) onSubmitQuery(v);
      else updateParams({ q: v });
    },
    [onSubmitQuery, updateParams],
  );

  const label = ariaLabel || t('home.searchPlaceholder', 'Search listings');
  const resolvedPlaceholder = placeholder || t('home.searchPlaceholder', 'Search listings');
  const fieldClass =
    className ||
    'h-12 w-full rounded-2xl border border-slate-200 bg-white pl-11 pr-10 text-base shadow-sm outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 sm:text-[15px]';

  if (!withChrome) {
    return (
      <input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={resolvedPlaceholder}
        className={className || 'w-full px-4 py-2 rounded-xl border'}
        aria-hidden={ariaHidden}
        aria-label={label}
      />
    );
  }

  // Phones: the field is a button that opens the full-screen search.
  if (withSuggestions && phone) {
    return (
      <div className="relative">
        <button
          type="button"
          onClick={() => {
            primeKeyboard();
            setOverlay(true);
          }}
          aria-label={label}
          aria-haspopup="dialog"
          className={`${fieldClass} flex items-center text-left`}
        >
          <span className={`block min-w-0 flex-1 truncate ${value ? 'text-slate-900' : 'text-slate-400'}`}>
            {value || resolvedPlaceholder}
          </span>
        </button>
        <Search size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
        {value && (
          <button
            type="button"
            onClick={() => submit('')}
            aria-label={t('filters.reset', 'Сбросить')}
            className="absolute right-2 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-full text-slate-400 transition active:scale-90 active:bg-slate-100"
          >
            <span className="grid h-5 w-5 place-items-center rounded-full bg-slate-300 text-white">
              <X size={12} strokeWidth={3} />
            </span>
          </button>
        )}
        {overlay && (
          <SearchScreen
            initial={value}
            placeholder={resolvedPlaceholder}
            onClose={() => setOverlay(false)}
            onSubmit={(q) => {
              setOverlay(false);
              submit(q);
            }}
          />
        )}
      </div>
    );
  }

  return (
    <DesktopSearch
      value={value}
      setValue={setValue}
      debounced={debounced}
      withSuggestions={withSuggestions}
      placeholder={resolvedPlaceholder}
      label={label}
      ariaHidden={ariaHidden}
      fieldClass={fieldClass}
      onSubmit={submit}
    />
  );
}

/**
 * iOS opens the keyboard only for a focus() made inside the tap itself, and
 * the search screen's input mounts a frame later. Focusing a throwaway input
 * now and moving focus to the real one keeps the keyboard up.
 */
function primeKeyboard() {
  const tmp = document.createElement('input');
  tmp.setAttribute('aria-hidden', 'true');
  tmp.style.cssText = 'position:fixed;top:0;left:0;opacity:0;height:0;font-size:16px;pointer-events:none;';
  document.body.appendChild(tmp);
  tmp.focus();
  setTimeout(() => tmp.remove(), 1000);
}

// ─── desktop / tablet: dropdown ──────────────────────────────────────────────

function DesktopSearch({
  value,
  setValue,
  debounced,
  withSuggestions,
  placeholder,
  label,
  ariaHidden,
  fieldClass,
  onSubmit,
}: {
  value: string;
  setValue: (v: string) => void;
  debounced: string;
  withSuggestions: boolean;
  placeholder: string;
  label: string;
  ariaHidden: boolean;
  fieldClass: string;
  onSubmit: (q: string) => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const listId = useId();
  const [focused, setFocused] = useState(false);
  const [active, setActive] = useState(-1);
  const { items, total, loading, settled } = useSuggestions(debounced, withSuggestions && focused);
  const price = usePriceLabel();
  const location = useLocationLabel();
  const open = withSuggestions && focused && value.trim().length >= MIN_CHARS && (loading || settled);

  useEffect(() => setActive(-1), [items]);

  return (
    <div className="relative">
      <Search size={18} className="pointer-events-none absolute left-4 top-1/2 z-[1] -translate-y-1/2 text-slate-400" />
      <input
        type="search"
        enterKeyHint="search"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            if (value) setValue('');
            else e.currentTarget.blur();
          } else if (open && e.key === 'ArrowDown') {
            e.preventDefault();
            setActive((i) => Math.min(items.length - 1, i + 1));
          } else if (open && e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((i) => Math.max(-1, i - 1));
          } else if (e.key === 'Enter') {
            e.preventDefault();
            const hit = active >= 0 ? items[active] : null;
            if (hit) {
              pushRecent(value);
              navigate(listingPath({ id: hit.id, title: hit.title }));
            } else onSubmit(value);
            e.currentTarget.blur();
          }
        }}
        placeholder={placeholder}
        autoComplete="off"
        autoCorrect="off"
        spellCheck={false}
        role={withSuggestions ? 'combobox' : undefined}
        aria-expanded={withSuggestions ? open : undefined}
        aria-controls={withSuggestions ? listId : undefined}
        aria-activedescendant={active >= 0 ? `${listId}-${active}` : undefined}
        className={`search-input ${fieldClass}`}
        aria-hidden={ariaHidden}
        aria-label={label}
      />
      {value && (
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setValue('')}
          aria-label={t('filters.reset', 'Сбросить')}
          className="absolute right-2.5 top-1/2 grid h-7 w-7 -translate-y-1/2 place-items-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
        >
          <X size={16} />
        </button>
      )}

      {open && (
        <div
          id={listId}
          role="listbox"
          className="absolute left-0 top-[calc(100%+8px)] z-50 w-full min-w-[min(420px,calc(100vw-2rem))] overflow-hidden rounded-2xl bg-white text-left shadow-[0_20px_60px_-15px_rgba(15,23,42,0.35)] ring-1 ring-slate-900/5"
          onMouseDown={(e) => e.preventDefault()}
        >
          {loading && items.length === 0 ? (
            <div className="py-1.5">
              <ResultSkeleton size={52} />
              <ResultSkeleton size={52} />
              <ResultSkeleton size={52} />
            </div>
          ) : items.length === 0 ? (
            <NoResults />
          ) : (
            <>
              <div className="max-h-[min(440px,60vh)] overflow-y-auto py-1.5">
                {items.map((item, i) => (
                  <Link
                    key={item.id}
                    id={`${listId}-${i}`}
                    role="option"
                    aria-selected={i === active}
                    to={listingPath({ id: item.id, title: item.title })}
                    onClick={() => pushRecent(value)}
                    onMouseEnter={() => setActive(i)}
                    className={`flex items-center gap-3 px-3.5 py-2 transition-colors ${i === active ? 'bg-slate-50' : ''}`}
                  >
                    <Thumb item={item} size={52} />
                    <span className="min-w-0 flex-1">
                      <span className="line-clamp-1 text-[14px] font-semibold text-slate-900">
                        {highlightMatch(item.title, debounced)}
                      </span>
                      {location(item) && (
                        <span className="mt-0.5 flex items-center gap-1 text-[12.5px] text-slate-500">
                          <MapPin size={12} className="shrink-0" />
                          <span className="truncate">{location(item)}</span>
                        </span>
                      )}
                    </span>
                    <span className="shrink-0 whitespace-nowrap pl-2 text-[14px] font-bold text-blue-600">{price(item)}</span>
                  </Link>
                ))}
              </div>
              <button
                type="button"
                onClick={() => onSubmit(value)}
                className="flex w-full items-center justify-between gap-2 border-t border-slate-100 px-4 py-3 text-[13.5px] font-semibold text-blue-600 transition hover:bg-blue-50/60"
              >
                {t('search.showAll', { count: total })}
                <ArrowRight size={16} />
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─── phones: full-screen search ──────────────────────────────────────────────

function SearchScreen({
  initial,
  placeholder,
  onClose,
  onSubmit,
}: {
  initial: string;
  placeholder: string;
  onClose: () => void;
  onSubmit: (q: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState(initial);
  const debounced = useDebounce(draft, 300);
  const [recent, setRecent] = useState(readRecent);
  const typing = draft.trim().length >= MIN_CHARS;
  const { items, total, loading, settled } = useSuggestions(debounced, true);
  const price = usePriceLabel();
  const location = useLocationLabel();
  const lang = (SEO_LANGS.find((l) => l.code === i18n.language)?.code ?? 'ru') as LangCode;
  const langPrefix = SEO_LANGS.find((l) => l.code === lang)?.urlPrefix || undefined;

  useBackHandler(true, onClose);

  useLayoutEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    input.focus({ preventScroll: true });
    const end = input.value.length;
    input.setSelectionRange(end, end);
  }, []);

  // The page underneath must not scroll or bounce while the search is open.
  useEffect(() => {
    const html = document.documentElement;
    const prev = html.style.overflow;
    html.style.overflow = 'hidden';
    return () => {
      html.style.overflow = prev;
    };
  }, []);

  const removeRecent = (q: string) => {
    const next = recent.filter((x) => x !== q);
    writeRecent(next);
    setRecent(next);
  };

  let body: ReactNode;
  if (!typing) {
    body = (
      <>
        {recent.length > 0 && (
          <section className="pt-2">
            <div className="flex items-center justify-between px-4 pb-1 pt-2">
              <h3 className="text-[13px] font-bold uppercase tracking-wide text-slate-400">{t('search.recent')}</h3>
              <button
                type="button"
                onClick={() => {
                  writeRecent([]);
                  setRecent([]);
                }}
                className="rounded-full px-2 py-1 text-[13px] font-semibold text-blue-600 active:bg-blue-50"
              >
                {t('search.clearRecent')}
              </button>
            </div>
            <ul>
              {recent.map((q) => (
                <li key={q} className="flex items-center">
                  <button
                    type="button"
                    onClick={() => onSubmit(q)}
                    className="flex min-w-0 flex-1 items-center gap-3 px-4 py-3 text-left active:bg-slate-50"
                  >
                    <Clock size={18} className="shrink-0 text-slate-400" />
                    <span className="truncate text-[16px] text-slate-800">{q}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => removeRecent(q)}
                    aria-label={t('search.removeRecent')}
                    className="mr-2 grid h-10 w-10 shrink-0 place-items-center rounded-full text-slate-400 active:bg-slate-100"
                  >
                    <X size={17} />
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}
        <section className="px-4 pb-6 pt-4">
          <h3 className="pb-3 text-[13px] font-bold uppercase tracking-wide text-slate-400">{t('search.categories')}</h3>
          <div className="grid grid-cols-2 gap-2.5">
            {SEO_CATEGORIES.map((c) => {
              const Icon = CATEGORY_ICONS[c.value] ?? Building2;
              return (
                <Link
                  key={c.slug}
                  to={hubPath(c.slug, undefined, undefined, langPrefix)}
                  onClick={onClose}
                  className="flex items-center gap-3 rounded-2xl bg-slate-50 px-3.5 py-3.5 ring-1 ring-slate-100 transition active:scale-[0.98] active:bg-slate-100"
                >
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-white text-blue-600 shadow-sm">
                    <Icon size={20} />
                  </span>
                  <span className="min-w-0 text-[14.5px] font-semibold leading-tight text-slate-800">{c.namesPlural[lang]}</span>
                </Link>
              );
            })}
          </div>
        </section>
      </>
    );
  } else if (items.length === 0 && (loading || !settled)) {
    body = (
      <div className="py-2">
        {Array.from({ length: 5 }, (_, i) => (
          <ResultSkeleton key={i} size={64} />
        ))}
      </div>
    );
  } else if (items.length === 0) {
    body = <NoResults />;
  } else {
    body = (
      <>
        <h3 className="px-4 pb-1 pt-4 text-[13px] font-bold uppercase tracking-wide text-slate-400">{t('search.results')}</h3>
        <ul className={`transition-opacity ${loading ? 'opacity-60' : ''}`}>
          {items.map((item) => (
            <li key={item.id}>
              <Link
                to={listingPath({ id: item.id, title: item.title })}
                onClick={() => {
                  pushRecent(draft);
                  onClose();
                }}
                className="flex items-center gap-3 px-4 py-2.5 active:bg-slate-50"
              >
                <Thumb item={item} size={64} />
                <span className="min-w-0 flex-1">
                  <span className="line-clamp-2 text-[15px] font-semibold leading-snug text-slate-900">
                    {highlightMatch(item.title, debounced)}
                  </span>
                  <span className="mt-1 flex items-center gap-2 text-[13px]">
                    <span className="shrink-0 font-bold text-blue-600">{price(item)}</span>
                    {location(item) && (
                      <span className="flex min-w-0 items-center gap-1 text-slate-500">
                        <MapPin size={12} className="shrink-0" />
                        <span className="truncate">{location(item)}</span>
                      </span>
                    )}
                  </span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
        <div className="px-4 pb-6 pt-3">
          <button
            type="button"
            onClick={() => onSubmit(draft)}
            className="flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-blue-600 text-[15px] font-bold text-white shadow-lg shadow-blue-600/20 transition active:scale-[0.99] active:bg-blue-700"
          >
            {t('search.showAll', { count: total })}
            <ArrowRight size={18} />
          </button>
        </div>
      </>
    );
  }

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('home.searchPlaceholder')}
      className="search-screen fixed inset-0 z-[180] flex flex-col bg-white"
    >
      <form
        role="search"
        onSubmit={(e) => {
          e.preventDefault();
          inputRef.current?.blur();
          onSubmit(draft);
        }}
        className="flex shrink-0 items-center gap-1.5 border-b border-slate-100 px-2 pb-2.5"
        style={{ paddingTop: 'calc(env(safe-area-inset-top) + 10px)' }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label={t('search.back')}
          className="grid h-11 w-11 shrink-0 place-items-center rounded-full text-slate-700 active:bg-slate-100"
        >
          <ArrowLeft size={22} />
        </button>
        <div className="relative min-w-0 flex-1">
          <Search size={18} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            ref={inputRef}
            type="search"
            enterKeyHint="search"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={placeholder}
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            aria-label={t('home.searchPlaceholder')}
            className="search-input h-11 w-full rounded-full border-0 bg-slate-100 pl-10 pr-10 text-[16px] text-slate-900 outline-none ring-0 placeholder:text-slate-400 focus:bg-slate-100 focus:ring-2 focus:ring-blue-500/30"
          />
          {draft && (
            <button
              type="button"
              onClick={() => {
                setDraft('');
                inputRef.current?.focus();
              }}
              aria-label={t('filters.reset', 'Сбросить')}
              className="absolute right-1.5 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-full"
            >
              <span className="grid h-5 w-5 place-items-center rounded-full bg-slate-400 text-white">
                <X size={12} strokeWidth={3} />
              </span>
            </button>
          )}
        </div>
      </form>
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
        {body}
      </div>
    </div>,
    document.body,
  );
}
